<template>
  <div class="input-wrapper px-3 pt-3 pb-3">
    <p class="calc-title">{{ $t("calculator.insert-quantity") }}</p>
    <div
      class="flex h-14 items-center gap-1 rounded-lg border border-input bg-white pr-1 pl-4"
    >
      <input
        class="min-w-0 flex-1 bg-transparent text-[26px] font-semibold tabular-nums outline-none"
        :value="inputContent"
        readonly
        inputmode="none"
        :aria-label="$t('calculator.insert-quantity')"
      />
      <SButton
        type="button"
        variant="ghost"
        size="icon"
        class="size-12 shrink-0 text-muted-foreground"
        aria-label="erase"
        @click="eraseFromInput()"
      >
        <!-- size-7 escapes SButton's svg clamp; --s-icon-size is what .s-icon sizes by -->
        <DeleteIcon class="size-7" style="--s-icon-size: 28px" />
      </SButton>
    </div>
  </div>

  <div
    class="grid grid-cols-3 gap-px overflow-hidden rounded-b-[5px] border-t border-border bg-border"
  >
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.1="updateInput"
    >
      1
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.2="updateInput"
    >
      2
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.3="updateInput"
    >
      3
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.4="updateInput"
    >
      4
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.5="updateInput"
    >
      5
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.6="updateInput"
    >
      6
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.7="updateInput"
    >
      7
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.8="updateInput"
    >
      8
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.9="updateInput"
    >
      9
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key"
      @click="updateInput"
      @keydown.0="updateInput"
    >
      0
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key text-muted-foreground"
      aria-label="hide calculator"
      @click="$emit('closeCalc')"
      @keydown.0="updateInput"
    >
      <EyeOffIcon class="size-6" style="--s-icon-size: 24px" />
    </SButton>
    <SButton
      type="button"
      variant="ghost"
      class="calc-key calc-key--clear"
      @click="clearInput"
    >
      {{ $t("calculator.clear").toUpperCase() }}
    </SButton>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import BookingModule from "@/store/modules/booking";
import { SButton } from "@smeetz/ui-core";
import { DeleteIcon, EyeOffIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: "NumericCalculator",
  components: { SButton, DeleteIcon, EyeOffIcon },
  props: ["visible"],
  emits: ["closeCalc"],
  setup(props) {
    const { t } = useI18n({});

    let inputContent = computed(() => {
      return BookingModule.getCalculatorValue;
    });

    let calcVisibility = ref(props.visible);

    function clearInput() {
      BookingModule.setCalcValue("0");
    }

    function eraseFromInput() {
      let length = inputContent.value.length;

      if (inputContent.value.length === 1) {
        if (inputContent.value[length - 1] !== "0") {
          let newInput = inputContent.value.replace(
            inputContent.value[length - 1],
            "0"
          );
          BookingModule.setCalcValue(newInput);
          return;
        }
        if (inputContent.value[length - 1] === "0") {
          return;
        }
      }
      BookingModule.setCalcValue(inputContent.value.slice(0, length - 1));
    }

    function updateInput(event: Event) {
      const char = ((event.target as HTMLElement).textContent || "").trim();
      if (inputContent.value === "0") {
        BookingModule.setCalcValue(char);
        return;
      }
      BookingModule.setCalcValue(inputContent.value + char);
    }

    return {
      inputContent,
      clearInput,
      eraseFromInput,
      updateInput,
      t,
      props,
      calcVisibility,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.input-wrapper {
  background-color: $background-color-clear;
}

.calc-title {
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  color: rgba(51, 51, 51, 0.55);
  margin-bottom: 8px;
}

// Keypad keys: flush, square, white with hover/pressed states.
// The 1px separators come from the gap-px + bg-border wrappers,
.calc-key {
  height: 58px;
  width: 100%;
  border-radius: 0;
  background-color: #fff;
  color: $color-black-3;
  font-size: 24px;
  font-weight: 400;

  &:hover {
    background-color: #f5f6f7;
  }

  &:active {
    background-color: #eceef0;
  }
}

.calc-key--clear {
  font-size: 16px;
  font-weight: 500;
  color: $color-green2;
}
</style>
